import {
    ChangeDetectionStrategy,
    Component,
    effect,
    ElementRef,
    OnDestroy,
    signal,
    viewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule } from '@angular/material/tooltip';
import { isJsonObject } from '@se/cp.common';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { ConfigData } from '../../models/configData';

const retreiveViewValue = (value: any, viewFieldName?: string): string => {
    if (!value) return '';
    if (isJsonObject(value) && viewFieldName) {
        if (!value[viewFieldName]) return '';
        else return value[viewFieldName].toString();
    }
    return value.toString();
};
export interface IEllipsisWithTooltipCellParams {
    viewFieldName?: string;
    isArray?: boolean;
}
@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'se-ellipsis-with-tooltip-cell-renderer',
    templateUrl: './ellipsis-with-tooltip-cell-renderer.component.html',
    styleUrls: ['./ellipsis-with-tooltip-cell-renderer.component.scss'],
    providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: ConfigData.tooltipConfiguration }],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatTooltipModule],
})
export class EllipsisWithTooltipCellRendererComponent implements ICellRendererAngularComp, OnDestroy {
    readonly params = signal<(ICellRendererParams & IEllipsisWithTooltipCellParams) | null>(null);
    readonly isArray = signal<boolean>(false);
    readonly viewValue = signal<string>('');
    readonly viewValueArray = signal<string[]>([]);
    readonly disabled = signal<boolean>(false);
    readonly cellContents = viewChildren<ElementRef<HTMLElement>>('cellContent');

    private ro?: ResizeObserver;

    constructor() {
        effect(() => {
            const cellContents = this.cellContents();
            if (cellContents) {
                requestAnimationFrame(() => this.enableOrDisableTooltip());
            }
        });
    }

    agInit(params: ICellRendererParams & IEllipsisWithTooltipCellParams): void {
        this.params.set(params);
        this.isArray.set(!!params.isArray);
        this.setViewValue(params.value, !!params.isArray, params.viewFieldName);
        this.ro?.disconnect();
        this.ro = new ResizeObserver((_) => {
            // Logica da eseguire quando la cella cambia dimensione
            requestAnimationFrame(() => this.enableOrDisableTooltip());
        });
        this.ro.observe(params.eGridCell); // Ascolta la cella stessa
    }

    refresh(params: ICellRendererParams & IEllipsisWithTooltipCellParams): boolean {
        this.setViewValue(params.value, !!params.isArray, params.viewFieldName);
        requestAnimationFrame(() => this.enableOrDisableTooltip());
        return true;
    }

    private enableOrDisableTooltip() {
        const params = this.params();
        const cellContents = this.cellContents();
        if (!params) return;
        if (!cellContents) return;
        if (!cellContents || cellContents.length === 0) {
            this.disabled.set(true);
        } else {
            const elementRefsArraySorted = [...cellContents].sort((a, b) => {
                let aOffsetWidth = a.nativeElement.offsetWidth;
                let bOffsetWidth = b.nativeElement.offsetWidth;
                return aOffsetWidth - bOffsetWidth;
            });
            const element = elementRefsArraySorted.pop()?.nativeElement;
            if (!element) return;
            const isTruncated = element.scrollWidth > element.clientWidth;
            this.disabled.set(!isTruncated);
        }
    }

    private setViewValue(value: any, isArray: boolean, viewFieldName?: string): void {
        if (!value) return;
        if (isArray && Array.isArray(value)) {
            const viewValueArray = value.map((el) => retreiveViewValue(el, viewFieldName));
            this.viewValueArray.set(viewValueArray);
        } else {
            const viewValue = retreiveViewValue(value, viewFieldName);
            this.viewValue.set(viewValue);
        }
    }

    ngOnDestroy(): void {
        this.ro?.disconnect();
        this.ro = undefined;
    }
}
